<script setup>
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
    XMarkIcon,
} from '@heroicons/vue/20/solid';
import { onMounted, onUnmounted, ref } from 'vue';

import AlertButton from './AlertButton.vue';

const emit = defineEmits(['hide']);

const props = defineProps({
    type: {
        type: String,
        default: 'info',
    },
    message: {
        type: String,
        default: '',
    },
    closable: {
        type: Boolean,
        default: true,
    },
});

const timeout = ref(null);

onMounted(() => {
    if (!props.closable) {
        return;
    }

    timeout.value = setTimeout(() => emit('hide'), 10000);
});

onUnmounted(() => {
    if (!props.closable) {
        return;
    }

    clearTimeout(timeout.value);
});
</script>

<template>
    <div
        class="rounded-xl p-4"
        :class="{
            'bg-success-50': type === 'success',
            'bg-brand-50': type === 'info',
            'bg-warning-50': type === 'warning',
            'bg-error-50': type === 'error',
        }"
    >
        <div class="flex">
            <div class="shrink-0">
                <CheckCircleIcon
                    v-if="type === 'success'"
                    class="size-5 text-success-600"
                    aria-hidden="true"
                />
                <InformationCircleIcon
                    v-else-if="type === 'info'"
                    class="size-5 text-brand-600"
                    aria-hidden="true"
                />
                <ExclamationTriangleIcon
                    v-else-if="type === 'warning'"
                    class="size-5 text-warning-600"
                    aria-hidden="true"
                />
                <XCircleIcon
                    v-else-if="type === 'error'"
                    class="size-5 text-error-600"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3">
                <p v-if="message" class="text-sm font-medium">
                    {{ message }}
                </p>
                <slot v-else />
                <div v-if="$slots.actions" class="mt-4">
                    <div class="-mx-2 -my-1.5 flex">
                        <slot name="actions" />
                    </div>
                </div>
            </div>
            <div v-if="closable" class="ml-auto pl-3">
                <div class="-mx-1.5 -my-1.5">
                    <AlertButton
                        :type="type"
                        class="inline-flex p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2"
                        @click="emit('hide')"
                    >
                        <span class="sr-only">{{ $t('layout.dismiss') }}</span>
                        <XMarkIcon class="size-5" aria-hidden="true" />
                    </AlertButton>
                </div>
            </div>
        </div>
    </div>
</template>
